import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CelloService } from '@app/services/cello.service';
import { CustomerIOService } from '@app/services/customer-io.service';
import { UserPreferencesService } from '@app/services/user-preferences.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { mgcIconGiftRegular } from '@sb/mgc-icons/mgcIcon-gift_regular.icon';
import { ButtonComponent } from '@sb/ui/button';
import { CheckboxComponent } from '@sb/ui/checkbox';
import { DialogRef, SbDialogModule } from '@sb/ui/dialog';
import { IconRegistry } from '@sb/ui/icon';
import { IconBadgeComponent } from '@sb/ui/icon-badge';
import { LaddaDirective } from '@sb/ui/ladda';
import { ToastService } from '@sb/ui/toast';
import { catchError, filter, finalize, from, switchMap } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'referral-opt-in-dialog',
  templateUrl: './referral-opt-in-dialog.component.html',
  imports: [
    SbDialogModule,
    ButtonComponent,
    TranslatePipe,
    LaddaDirective,
    FormsModule,
    CheckboxComponent,
    IconBadgeComponent,
  ],
  providers: [UserPreferencesService],
})
export class ReferralOptInDialogComponent {
  private dialogRef = inject(DialogRef);
  private userPreferencesService = inject(UserPreferencesService);
  private celloService = inject(CelloService);
  private customerIoService = inject(CustomerIOService);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);

  isSaving = signal(false);
  optIn = false;

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconGiftRegular]);
  }

  submit() {
    this.isSaving.set(true);
    void this.userPreferencesService
      .setReferralConsent(this.optIn)
      .pipe(
        // Initialize Cello only if the user opted in
        filter(() => this.optIn),
        switchMap(() => {
          this.updateTraitsInCustomerIo();
          return from(this.celloService.initialize());
        }),
        finalize(() => {
          this.isSaving.set(false);
        }),
        catchError(() => {
          this.toast.openDanger(
            this.translate.instant('Something went wrong trying to connect you to our referral program'),
          );
          return undefined;
        }),
      )
      .subscribe(() => {
        this.dialogRef.close(this.optIn);
      });
  }

  private updateTraitsInCustomerIo() {
    this.customerIoService.updateUserAttributes({
      referral_program_joined: true,
      referral_program_joined_at: Math.floor(Date.now() / 1000), // Unix timestamp
      referral_source: 'Cello',
    });
  }
}
